/**
 * Main metrics orchestrator — two-layer engine (CONF-147 subtask 4).
 *
 * Core: generateMetricsFromDomain(domain, productDb, metricsDb) reads the typed
 * domain boxes. This is the seam later subtasks (App.js read-site migration)
 * call directly once call sites hold domain objects.
 *
 * Facade: generateMetrics(flatConfig, productDb, metricsDb) keeps the public
 * flat signature for today's callers (useConfigurationLogic)
 * and maps flat → domain via the ConfigurationMapper anti-corruption layer.
 * Note: no schema migration here — the facade's input is the live runtime flat
 * config (always current-shaped); migrateToCurrentSchema belongs to the
 * persisted-quote load path.
 *
 * Output stays today's flat metrics object; restructuring it is the epic's
 * declared fast-follow.
 */

import calculateDirectorMetrics from './calculators/directorMetrics';
import calculateHybridNodeMetrics, { hybridThroughputFactors } from './calculators/hybridNodeMetrics';
import calculateAfaNodeMetrics, { afaThroughputFactors } from './calculators/afaNodeMetrics';
import calculateSystemMetrics from './calculators/systemMetrics';
import { fromFlatConfig } from '../domain';
import type { DomainConfiguration, FlatConfig, HybridStorageBlock, SystemConfiguration } from '../domain';
import type { MetricsContext, ProductDb, MetricsDb, NodeMetricsBag, SsdMediaType, ThroughputBasis } from './types';
import { mlecEligible } from '../domain/rules/mlecRules';
import { dualCpuFlashEligible } from '../domain/rules/dualCpuRules';
import { ssdMediaType } from '../utils/ssdMediaType';
import { getRackHeights } from '../config/vendorRegistry';
import { log } from '../utils/devLog';

// Flat output object (spec §1: output shape unchanged this subtask; typing the
// metrics themselves is the fast-follow).
export type FlatMetrics = Record<string, any>;

/**
 * Cross-box inputs the calculators need, resolved once per domain so every
 * calculator and `computeThroughputBasis` agree on the same facts (CONF-258).
 */
export const buildMetricsContext = (domain: DomainConfiguration, productDb: ProductDb): MetricsContext => {
  const system = domain.system;
  return {
    quarter: domain.pricing.quarter,
    hardwareVendor: system.hardwareVendor,
    isPBUnit: domain.viewState.isPBUnit,
    // CONF-93: the flash calculators resolve throughput as
    // min(card ceiling, protocol rate) from these two.
    networkOption: system.interfaceOption,
    networkProtocol: system.networkProtocol,
    // CONF-210: the same eligibility backstop as the MLEC clamp below. R13
    // reverts an ineligible 'dualCpu' in the rules pipeline, but the AI
    // optimizer and a vendor-switched legacy quote reach this seam without the
    // rules having run, so the model is resolved here and the calculator
    // downstream carries no guard of its own.
    afaNodeModel: system.afa.nodeModel === 'dualCpu' && dualCpuFlashEligible(system.hardwareVendor)
      ? 'dualCpu'
      : 'standard',
    // CONF-221: resolved here, not in the calculator, for the same reason as
    // afaNodeModel — the calculator takes facts, not lookups. An unstamped or
    // unknown part is TLC.
    afaSsdMediaType: ssdMediaType(system.hardwareVendor, system.afa.ssdPartNumber) as SsdMediaType,
    // CONF-230: throws for a vendor with no heights rather than assuming 1U.
    rackHeights: getRackHeights(system.hardwareVendor),
  };
};

/**
 * CONF-94: the eligibility backstop. R12 clears an ineligible MLEC flag in the
 * rules pipeline, but any caller that builds a candidate with {...config} and
 * calls generateMetrics without running the rules (the retired AI optimizer
 * did exactly that, CONF-232) would let a user's hybridMLEC: true ride into a
 * jbodSize: 78 candidate. Clamping at this seam covers that path, loaded
 * legacy quotes and any future caller, which is why the calculators and
 * `computeThroughputBasis` downstream carry no MLEC guards of their own.
 *
 * NOT done in ConfigurationMapper: that layer is asserted lossless by
 * ConfigurationMapper.test.ts ('full config round-trips losslessly'), and
 * clamping there would turn hybridMLEC: true into false on a round trip.
 */
export const effectiveHybridBlock = (system: SystemConfiguration): HybridStorageBlock =>
  mlecEligible(system.hardwareVendor, system.hybrid.jbodSize)
    ? system.hybrid
    : { ...system.hybrid, mlec: false };

/**
 * Generate all system metrics from the typed domain configuration.
 */
export const generateMetricsFromDomain = (
  domain: DomainConfiguration,
  productDb: ProductDb,
  metricsDb: MetricsDb,
): FlatMetrics => {
  const system = domain.system;
  const ctx: MetricsContext = buildMetricsContext(domain, productDb);

  // Initialize metrics collection
  const nodeMetrics: NodeMetricsBag = {};

  // ========== CALCULATE DIRECTOR METRICS ==========
  // Directors are always present (manage the system)
  // MORK TODO : if systemType is Expansion, directors might not be included
  try {
    nodeMetrics.directorMetrics = calculateDirectorMetrics(system.director, ctx, productDb, metricsDb);
  } catch (error) {
    console.error('Error calculating director metrics:', error);
    nodeMetrics.directorMetrics = {};
  }

  // ========== CALCULATE HYBRID NODE METRICS ==========
  // Only if hybrid storage is enabled
  if (system.hybrid.enabled && system.hybrid.nodeCount > 0) {
    const hybrid = effectiveHybridBlock(system);
    try {
      nodeMetrics.hybridMetrics = calculateHybridNodeMetrics(hybrid, ctx, productDb, metricsDb);
    } catch (error) {
      console.error('Error calculating hybrid node metrics:', error);
      nodeMetrics.hybridMetrics = {};
    }
  }

  // ========== CALCULATE AFA NODE METRICS ==========
  // Only if AFA storage is enabled
  if (system.afa.enabled && system.afa.nodeCount > 0) {
    try {
      nodeMetrics.afaMetrics = calculateAfaNodeMetrics(system.afa, ctx, productDb, metricsDb);
    } catch (error) {
      console.error('Error calculating AFA node metrics:', error);
      nodeMetrics.afaMetrics = {};
    }
  }

  // ========== CALCULATE SYSTEM-WIDE METRICS ==========
  let systemMetrics: FlatMetrics = {};
  try {
    systemMetrics = calculateSystemMetrics(system, ctx, productDb, metricsDb, nodeMetrics);
  } catch (error) {
    console.error('Error calculating system metrics:', error);
  }

  // ========== COMBINE ALL METRICS ==========
  // Merge node-level and system-level metrics into single object
  const allMetrics: FlatMetrics = {
    ...nodeMetrics.directorMetrics,
    ...nodeMetrics.hybridMetrics,
    ...nodeMetrics.afaMetrics,
    ...systemMetrics
  };

  // Debug logging if enabled
  log('=== METRICS CALCULATION COMPLETE ===');
  log('Director Metrics:', nodeMetrics.directorMetrics);
  log('Hybrid Metrics:', nodeMetrics.hybridMetrics);
  log('AFA Metrics:', nodeMetrics.afaMetrics);
  log('System Metrics:', systemMetrics);
  log('All Metrics:', allMetrics);

  return allMetrics;
};

/**
 * CONF-258: the raw per-node throughput rating and multiplier behind each
 * system-total throughput figure, derived with the exact same context,
 * eligibility clamp and gating (`enabled && nodeCount > 0`) as
 * `generateMetricsFromDomain` — so `rating * multiplier` reproduces the
 * matching total exactly. `rating` is the figure before write efficiency and
 * before MLEC derating (canonical GB/s); `multiplier` folds node count and
 * whichever of those two apply. A disabled set, or one with 0 nodes, keys
 * are simply absent — callers must not assume a default.
 */
export const computeThroughputBasis = (
  domain: DomainConfiguration,
  productDb: ProductDb,
  metricsDb: MetricsDb,
): ThroughputBasis => {
  const system = domain.system;
  const ctx: MetricsContext = buildMetricsContext(domain, productDb);
  const basis: ThroughputBasis = {};

  // Each branch mirrors generateMetricsFromDomain's own try/catch around the
  // same calculators: bad/legacy data (e.g. a vendor-switched quote whose
  // jbodSize has no metricsDb.hybrid[jbodKey] entry) must degrade to that
  // set's basis keys being absent, not throw and take deriveOutputs — and
  // the headless MCP session path — down with it.
  if (system.afa.enabled && system.afa.nodeCount > 0) {
    try {
      const f = afaThroughputFactors(system.afa, ctx, metricsDb);
      basis.afaReadPerNode = { rating: f.read, multiplier: system.afa.nodeCount };
      basis.afaWritePerNode = { rating: f.write, multiplier: system.afa.nodeCount * f.writeEfficiency };
    } catch (error) {
      console.error('Error calculating AFA throughput basis:', error);
    }
  }

  if (system.hybrid.enabled && system.hybrid.nodeCount > 0) {
    try {
      const hybrid = effectiveHybridBlock(system);
      const f = hybridThroughputFactors(hybrid, ctx, productDb, metricsDb);
      basis.hybridHddReadPerNode = { rating: f.hddRead, multiplier: hybrid.nodeCount * f.mlecDerate };
      basis.hybridHddWritePerNode = {
        rating: f.hddWrite,
        multiplier: hybrid.nodeCount * f.mlecDerate * f.writeEfficiency,
      };
      basis.hybridNvmeReadPerNode = { rating: f.nvmeRead, multiplier: hybrid.nodeCount };
      basis.hybridNvmeWritePerNode = { rating: f.nvmeWrite, multiplier: hybrid.nodeCount * f.writeEfficiency };
    } catch (error) {
      console.error('Error calculating hybrid throughput basis:', error);
    }
  }

  return basis;
};

/**
 * Generate all system metrics (public flat facade — signature unchanged).
 */
export const generateMetrics = (
  config: FlatConfig | null | undefined,
  productDb: ProductDb,
  metricsDb: MetricsDb,
): FlatMetrics => {
  // Validate inputs
  if (!config || !productDb || !metricsDb) {
    console.error('Missing required parameters for generateMetrics');
    return {};
  }

  return generateMetricsFromDomain(fromFlatConfig(config), productDb, metricsDb);
};

// Default export for convenience
export default generateMetrics;
